<script>
    import { browser } from '$app/environment';
    import { onNavigate } from '$app/navigation';
    import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
    import Icon from './Icon.svelte';
    import {
        is_top_modal,
        register_modal,
        unregister_modal
    } from '$lib/utils/modal_state';

    let className = '';
    export { className as class };
    export let show_close = true;
    export let close_on_background = true;

    let overlay_el;
    let modal_target;
    let is_destroyed = false;
    let is_registered = false;
    let dispatch = createEventDispatcher();

    export const close = () => {
        if (overlay_el instanceof HTMLElement && !is_top_modal(overlay_el)) {
            return;
        }

        dispatch('close');
    };

    const handle_esc = (e) => {
        if (
            e.key === 'Escape' &&
            overlay_el instanceof HTMLElement &&
            is_top_modal(overlay_el)
        ) {
            close();
        }
    };

    onMount(async () => {
        if (!browser) {
            return;
        }

        await tick();

        if (is_destroyed || !(overlay_el instanceof HTMLElement)) {
            return;
        }

        modal_target = document.querySelector('#modal-target');

        if (!(modal_target instanceof HTMLElement)) {
            return;
        }

        is_registered = true;
        modal_target.appendChild(overlay_el);
        register_modal(overlay_el);

        document.addEventListener('keydown', handle_esc);
    });

    onDestroy(() => {
        is_destroyed = true;

        if (!browser) {
            return;
        }

        if (is_registered && overlay_el instanceof HTMLElement) {
            unregister_modal(overlay_el);
        }

        if (
            overlay_el instanceof HTMLElement &&
            modal_target instanceof HTMLElement &&
            overlay_el.parentNode === modal_target
        ) {
            modal_target.removeChild(overlay_el);
        }

        unregister_modal(overlay_el);
        document.removeEventListener('keydown', handle_esc);
        is_registered = false;
    });

    onNavigate(async () => {
        if (is_registered) {
            unregister_modal(overlay_el);
            is_registered = false;
        }

        if (
            overlay_el instanceof HTMLElement &&
            modal_target instanceof HTMLElement &&
            overlay_el.parentNode === modal_target
        ) {
            modal_target.removeChild(overlay_el);
        }

        dispatch('close');
        await tick();
    });
</script>

<div bind:this={overlay_el} class="fixed inset-0 z-10 overflow-hidden">
    {#if close_on_background}
        <button
            type="button"
            aria-label="Close overlay"
            on:click={close}
            class="absolute inset-0 bg-black/70"
        />
    {:else}
        <div aria-hidden="true" class="absolute inset-0 bg-black/70" />
    {/if}

    {#if show_close}
        <button
            type="button"
            aria-label="Close overlay"
            on:click={close}
            class="overlay-close"
        >
            <span class="sr-only">Close overlay</span>
            <Icon times class="block bg-black/50 rounded-full w-8 h-8 p-1" />
        </button>
    {/if}

    <div class="relative z-10 w-full h-full {className}">
        <slot />
    </div>
</div>

<style>
    .overlay-close {
        position: fixed;
        top: max(env(safe-area-inset-top), 0.5rem);
        right: max(env(safe-area-inset-right), 0.75rem);
        z-index: 20;
    }
</style>
